import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BrainIcon, ChevronLeftIcon, ChevronRightIcon, ClipboardListIcon, FileTextIcon, HeadphonesIcon, ListChecksIcon, SaladIcon, ShieldCheckIcon } from 'lucide-react';
import { Section } from './Shared';
type PreviewSlide = {
  label: string;
  title: string;
  text: string;
  image: string;
  alt: string;
  icon: typeof FileTextIcon;
};
const slides: PreviewSlide[] = [{
  label: 'FITOTERAPIA RESPONSABLE',
  title: 'Guía de seguridad y señales de alerta',
  text: 'Criterios de cuidado, registro y derivación para abordar cada recurso de forma responsable.',
  image: 'https://cdn.magicpatterns.com/uploads/pewzhEd5td7TwonQSpzg1z/Screenshot_20260717-160440~2.png',
  alt: 'Página de seguridad de fitoterapia con infusión de hierbas',
  icon: ShieldCheckIcon
}, {
  label: 'FITOTERAPIA NATIVA',
  title: 'Proporciones orientativas y registro',
  text: 'Una página de consulta para organizar preparaciones y observaciones dentro de un marco complementario.',
  image: 'https://cdn.magicpatterns.com/uploads/oXBTUbaVmxDZExZtumhaMm/Screenshot_20260717-160638~2.png',
  alt: 'Página de orientaciones para preparaciones de fitoterapia',
  icon: FileTextIcon
}, {
  label: 'RECETARIO CRIOLLO',
  title: 'Menús y recetas de 21 días',
  text: 'Preparaciones paso a paso, porciones y propuestas cotidianas para organizar el material de consulta.',
  image: 'https://cdn.magicpatterns.com/uploads/cVkiNG1Wbc5AtpRdHuakbz/Screenshot_20260717-160857~2.png',
  alt: 'Página de recetario con receta de panqueques y pollo con ensalada',
  icon: SaladIcon
}, {
  label: 'WORKSHEETS',
  title: 'Exploración y reflexión guiada',
  text: 'Preguntas de journaling para acompañar la conversación y el registro emocional de manera complementaria.',
  image: 'https://cdn.magicpatterns.com/uploads/bLAxQa7ZDs5DgBtLC1YvN7/Screenshot_20260717-160951~2.png',
  alt: 'Página de worksheet de reflexión con preguntas de journaling',
  icon: BrainIcon
}, {
  label: 'PLANIFICADOR',
  title: 'Registro consciente diario',
  text: 'Un formato ordenado para hábitos, comidas, descanso, emociones y los datos que corresponda registrar.',
  image: 'https://cdn.magicpatterns.com/uploads/eKtVLXAJTZxBnxrrPTFBCM/Screenshot_20260717-161040~2.png',
  alt: 'Página de registro consciente diario con tablas de seguimiento',
  icon: ClipboardListIcon
}, {
  label: 'MEDITACIONES',
  title: 'Plan de práctica de 21 días',
  text: 'Una guía para integrar prácticas de calma y observación personal a lo largo de tres semanas.',
  image: 'https://cdn.magicpatterns.com/uploads/w5smZrqxg7A9qA22EqBu2f/Screenshot_20260717-161128~2.png',
  alt: 'Página de plan de 21 días para crear el hábito de la calma',
  icon: HeadphonesIcon
}, {
  label: 'SUPLEMENTACIÓN',
  title: 'Fichas educativas y precauciones',
  text: 'Material de referencia para conversaciones responsables sobre minerales, siempre con criterio profesional.',
  image: 'https://cdn.magicpatterns.com/uploads/bzKzyuKfZkt3rnwkfsvekm/Screenshot_20260717-161205~2.png',
  alt: 'Página de ficha técnica sobre cromo y precauciones críticas',
  icon: ListChecksIcon
}];
export function MaterialPreview() {
  const [index, setIndex] = useState(0);
  const next = () => setIndex(value => (value + 1) % slides.length);
  const prev = () => setIndex(value => (value - 1 + slides.length) % slides.length);
  useEffect(() => {
    const timer = window.setInterval(next, 6000);
    return () => window.clearInterval(timer);
  }, []);
  const slide = slides[index];
  const Icon = slide.icon;
  return <Section data-id="element-180" data-mp="1cxmzvvxua8lc">
      <div className="mx-auto max-w-5xl" data-id="element-181" data-mp="1cx4xsiohx05i">
        <div className="text-center" data-id="element-182" data-mp="1cy6kjkevjb32">
          <p className="text-xs font-bold tracking-[.18em] text-brand-goldDark uppercase" data-id="element-183" data-mp="17z3fhzyw94v3">Vista previa del Mega Pack</p>
          <h2 className="mt-3 text-3xl font-bold text-brand-ink md:text-4xl" data-id="element-184" data-mp="37c7k67i90247">Conocé el material por dentro</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-brand-muted" data-id="element-185" data-mp="32dw6flou17gp">Páginas reales para usar digitalmente, imprimir o integrar en tu organización de consulta.</p>
        </div>

        <div className="mt-9 grid overflow-hidden border-2 border-brand-ink bg-brand-ink md:grid-cols-[.85fr_1.15fr]" data-id="element-186" data-mp="241a6y2svjq5t">
          <div className="relative flex min-h-[390px] items-center justify-center bg-brand-surface p-5 sm:p-7" data-id="element-187" data-mp="1q0h8j6zgcaxf">
            <AnimatePresence mode="wait" data-id="element-188" data-mp="1uysjxm5eathr">
              <motion.img key={slide.image} src={slide.image} alt={slide.alt} initial={{
              opacity: 0,
              scale: 0.98
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 1.02
            }} transition={{
              duration: 0.28
            }} className="max-h-[460px] w-full object-contain shadow-xl" draggable={false} data-id="element-189" data-mp="1gbpmffh2okuf" />
            </AnimatePresence>
            <span className="absolute bottom-4 left-4 bg-brand-ink px-2 py-1 text-[10px] font-bold tracking-[.12em] text-brand-gold uppercase" data-id="element-190" data-mp="xeoepmw4w6ms">Página {index + 1} de {slides.length}</span>
          </div>

          <div className="relative flex min-h-[360px] flex-col justify-between p-7 text-white md:p-10" data-id="element-191" data-mp="irz7szzjyr6d">
            <AnimatePresence mode="wait" data-id="element-192" data-mp="dt623tqic4gr">
              <motion.div key={slide.title} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.28
            }} data-id="element-193" data-mp="soc7qfloy19k">
                <div className="flex h-11 w-11 items-center justify-center border border-brand-gold text-brand-gold" data-id="element-194" data-mp="wow7zs1k9kzx"><Icon className="h-5 w-5" data-id="element-195" data-mp="1bd8ulyerdrhr" /></div>
                <p className="mt-8 text-xs font-bold tracking-[.18em] text-brand-gold uppercase" data-id="element-196" data-mp="16fgzkvithp9c">{slide.label}</p>
                <h3 className="mt-3 font-display text-3xl font-bold leading-tight" data-id="element-197" data-mp="1keqhq0ql2tfy">{slide.title}</h3>
                <p className="mt-5 max-w-sm text-base leading-relaxed text-white/72" data-id="element-198" data-mp="1ge6lczdu3tuv">{slide.text}</p>
                <div className="mt-7 border-l-2 border-brand-gold pl-4 text-xs leading-relaxed text-brand-goldLight" data-id="element-199" data-mp="1nhd3dn13082p">Material educativo y complementario. No reemplaza diagnóstico, tratamiento, medicación ni orientación médica profesional.</div>
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center justify-between" data-id="element-200" data-mp="2269c7maypp4d">
              <div className="flex gap-2" role="tablist" aria-label="Páginas de vista previa" data-id="element-201" data-mp="227ke1gkukmsu">
                {slides.map((preview, slideIndex) => <button key={preview.image} onClick={() => setIndex(slideIndex)} aria-label={`Ver ${preview.title}`} aria-selected={index === slideIndex} className={`h-2.5 transition-all ${index === slideIndex ? 'w-8 bg-brand-gold' : 'w-2.5 bg-white/35 hover:bg-white/70'}`} data-id="element-202" data-mp="2b5wahz8eaet7" data-mpi={slideIndex} />)}
              </div>
              <div className="flex gap-2" data-id="element-203" data-mp="ls0xuf0cim3t">
                <button onClick={prev} aria-label="Vista previa anterior" className="flex h-10 w-10 items-center justify-center border border-white/30 transition-colors hover:border-brand-gold hover:text-brand-gold" data-id="element-204" data-mp="ltc2eh1m7xvz"><ChevronLeftIcon className="h-5 w-5" data-id="element-205" data-mp="34258frzenwpy" /></button>
                <button onClick={next} aria-label="Vista previa siguiente" className="flex h-10 w-10 items-center justify-center border border-white/30 transition-colors hover:border-brand-gold hover:text-brand-gold" data-id="element-206" data-mp="2z9vkuilrj8qu"><ChevronRightIcon className="h-5 w-5" data-id="element-207" data-mp="2qa8m64kf4h1h" /></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>;
}